import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import {
  CloseRounded,
  UploadFileRounded,
  DeleteOutlineRounded,
  SendRounded,
} from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../../lib/firebase';
import {
  assignmentApi,
  type AssignmentSummaryDto,
  type SubmissionFileDto,
} from '../../../lib/api/assignment.api';
import {
  useTenantTimeZone,
  useTenantDateFormat,
  useTenantTimeFormat,
} from '../../../stores/tenant.store';
import { formatInstant } from '@dosfilos/shared';
import { errorText } from '../../../lib/error-text';

interface Props {
  tenantId: string;
  assignment: AssignmentSummaryDto;
  onClose: () => void;
  onSubmitted: () => void;
}

// Uploads are restricted to PDF so the work is always viewable in the review surface.
const MAX_BYTES = 50 * 1024 * 1024;

export function SubmitAssignmentDialog({
  tenantId,
  assignment,
  onClose,
  onSubmitted,
}: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [files, setFiles] = useState<SubmissionFileDto[]>([]);
  const [textBody, setTextBody] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The submission is already submitted/graded (not a draft). Editing it here would
  // 500 with "Only draft submissions can be edited"; show a read-only notice instead
  // and let the parent refresh so the CTA reflects the real state.
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  /** El docente cerró la nota SIN que este alumno entregara. No es una entrega:
   *  mientras la tarea siga abierta todavía puede entregar, y su trabajo
   *  reemplaza esa nota. Antes se le decía «ya entregaste», en verde, a quien
   *  nunca entregó. */
  const [notaSinEntrega, setNotaSinEntrega] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    assignmentApi
      .getOrCreateDraft({ tenantId, assignmentId: assignment.id })
      .then(async ({ id }) => {
        setSubmissionId(id);
        const s = await assignmentApi.get({ tenantId, submissionId: id });
        setFiles(s.files);
        setTextBody(s.textBody ?? '');
        setUrl(s.url ?? '');
        // A non-draft submission already exists → this is a stale "submit" entry.
        // Una nota puesta sin entrega NO cuenta: ese registro no es trabajo del
        // alumno y la puerta sigue abierta hasta el cierre de la tarea.
        setAlreadySubmitted(s.status !== 'draft' && !s.absent);
        setNotaSinEntrega(!!s.absent);
      })
      .catch((e) => setErr(errorText(e, t('portal.submitAssignment.errorGeneric'))))
      .finally(() => setLoading(false));
  }, [tenantId, assignment.id]);

  const handleFilePick = async (selected: FileList | null) => {
    if (alreadySubmitted || !selected || !submissionId) return;
    const file = selected[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setErr(
        t('portal.submitAssignment.fileTooLarge', {
          selected: (file.size / 1024 / 1024).toFixed(1),
        }),
      );
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setErr(t('portal.submitAssignment.fileTypePdfOnly'));
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      const safeName = file.name.replace(/[^\w.\-]/g, '_');
      const storagePath = `tenants/${tenantId}/submissions/${submissionId}/${Date.now()}/${safeName}`;
      const sref = ref(storage, storagePath);
      await uploadBytes(sref, file, { contentType: file.type });
      const next: SubmissionFileDto[] = [
        ...files,
        {
          storagePath,
          originalFileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          uploadedAt: new Date().toISOString(),
        },
      ];
      setFiles(next);
      await assignmentApi.updateDraft({
        tenantId,
        submissionId,
        files: next,
      });
    } catch (e) {
      setErr(errorText(e, t('portal.submitAssignment.errorUpload')));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = async (storagePath: string) => {
    if (alreadySubmitted || !submissionId) return;
    const next = files.filter((f) => f.storagePath !== storagePath);
    setFiles(next);
    await assignmentApi.updateDraft({
      tenantId,
      submissionId,
      files: next,
    });
  };

  // Map a callable error to a message, surfacing the machine block reason
  // (`assignment.locked`) so a hard freeze isn't reported as a generic failure.
  const errMessage = (e: unknown, fallbackKey: string): string => {
    const reason = (e as { details?: { reason?: string } })?.details?.reason;
    if (reason === 'assignment.locked') return t('portal.submitAssignment.errorLocked');
    return errorText(e, t(fallbackKey));
  };

  const saveDraft = async () => {
    if (alreadySubmitted || !submissionId) return;
    setSavingDraft(true);
    setErr(null);
    try {
      await assignmentApi.updateDraft({
        tenantId,
        submissionId,
        files,
        textBody,
        url,
      });
    } catch (e) {
      setErr(lockedNow() ? closedNotice : errMessage(e, 'portal.submitAssignment.errorGeneric'));
    } finally {
      setSavingDraft(false);
    }
  };

  const submitFinal = async () => {
    // Already submitted → nothing to do; just refresh the parent's view.
    if (alreadySubmitted) {
      onSubmitted();
      return;
    }
    if (!submissionId) return;
    if (files.length === 0 && !textBody.trim() && !url.trim()) {
      setErr(t('portal.submitAssignment.errorEmptySubmission'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await assignmentApi.updateDraft({
        tenantId,
        submissionId,
        files,
        textBody,
        url,
      });
      await assignmentApi.submit({ tenantId, submissionId });
      onSubmitted();
    } catch (e) {
      setErr(lockedNow() ? closedNotice : errMessage(e, 'portal.submitAssignment.errorSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  const accepts = assignment.submissionTypes;
  // Misconfigured assignment: an upload-based type with no channel renders an
  // empty dialog. Say so instead of showing a submit button that then fails
  // with "empty submission".
  const noChannel =
    accepts.length === 0 &&
    (assignment.type === 'reading-review' ||
      assignment.type === 'essay' ||
      assignment.type === 'project' ||
      assignment.type === 'other');
  const isPastDue = assignment.dueAt && new Date(assignment.dueAt) < new Date();
  // Hard close (lockAt) passed → no submission accepted. The server also rejects
  // (getOrCreateDraft/submit check isLocked); this gates the UI so the student
  // can't pick a file or submit, and sees a localized notice (not the raw error).
  const lockedNow = (): boolean =>
    !!assignment.lockAt && new Date(assignment.lockAt).getTime() < Date.now();
  const closed = lockedNow();
  const closedNotice = assignment.lockAt
    ? t('portal.submitAssignment.closedWithDate', {
        date: formatInstant(assignment.lockAt, tenantTz, tenantFmt, tenantTimeFmt),
      })
    : t('portal.submitAssignment.closed');

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {assignment.name}
          </Typography>
          {isPastDue && (
            <Chip size="small" label={t('portal.submitAssignment.pastDueChip')} color="warning" />
          )}
          <IconButton onClick={onClose} disabled={submitting}>
            <CloseRounded />
          </IconButton>
        </Stack>
        {assignment.dueAt && (
          <Typography variant="caption" color="text.secondary">
            {t('portal.submitAssignment.dueMeta', {
              date: formatInstant(assignment.dueAt, tenantTz, tenantFmt, tenantTimeFmt),
              points: assignment.pointsPossible,
            })}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        ) : closed ? (
          <Alert severity="error">{closedNotice}</Alert>
        ) : noChannel ? (
          <Alert severity="warning">{t('portal.submitAssignment.noChannel')}</Alert>
        ) : (
          <Stack spacing={2.5}>
            {err && (
              <Alert severity="error" onClose={() => setErr(null)}>
                {err}
              </Alert>
            )}

            {alreadySubmitted && (
              <Alert severity="success">{t('portal.submitAssignment.alreadySubmitted')}</Alert>
            )}

            {notaSinEntrega && !alreadySubmitted && (
              <Alert severity="warning">
                {t('portal.submitAssignment.gradedWithoutSubmission')}
              </Alert>
            )}

            {accepts.includes('file') && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  {t('portal.submitAssignment.filesLabel')}
                </Typography>
                {files.map((f) => (
                  <Stack
                    key={f.storagePath}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ px: 1, py: 0.75, bgcolor: 'action.hover', borderRadius: 1, mb: 0.5 }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                      {f.originalFileName}
                    </Typography>
                    {f.sizeBytes !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        {t('portal.submitAssignment.fileSizeMb', {
                          size: (f.sizeBytes / 1024 / 1024).toFixed(1),
                        })}
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFile(f.storagePath)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => handleFilePick(e.target.files)}
                />
                <Button
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileRounded />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  {uploading
                    ? t('portal.submitAssignment.uploading')
                    : t('portal.submitAssignment.uploadButton')}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  {t('portal.submitAssignment.uploadHint')}
                </Typography>
              </Box>
            )}

            {accepts.includes('text') && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  {t('portal.submitAssignment.textLabel')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={textBody}
                  onChange={(e) => setTextBody(e.target.value)}
                  placeholder={t('portal.submitAssignment.textPlaceholder')}
                />
              </Box>
            )}

            {accepts.includes('url') && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  {t('portal.submitAssignment.urlLabel')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t('portal.submitAssignment.urlPlaceholder')}
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {alreadySubmitted ? (
          <Button variant="contained" color="primary" onClick={onSubmitted}>
            {t('portal.submitAssignment.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>
              {t('portal.submitAssignment.cancel')}
            </Button>
            {!closed && !noChannel && (
              <>
                <Button
                  onClick={saveDraft}
                  disabled={loading || submitting || savingDraft}
                  startIcon={savingDraft ? <CircularProgress size={16} /> : undefined}
                >
                  {savingDraft
                    ? t('portal.submitAssignment.saving')
                    : t('portal.submitAssignment.saveDraft')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submitFinal}
                  disabled={loading || submitting}
                  startIcon={submitting ? <CircularProgress size={16} /> : <SendRounded />}
                >
                  {submitting
                    ? t('portal.submitAssignment.submitting')
                    : t('portal.submitAssignment.submit')}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
